// ─────────────────────────────────────────────────────────────────────────────
// FASE 4 (paridad demo) — Resuelve el <img> real de un Fundamental/Comportamiento.
//
// El demo (SOLO LECTURA) pinta siempre un SVG real: `f.icono` para Fundamentales y
// `behavior.icon` para Comportamientos (ReconocerPanel.tsx del demo, líneas 9-17 y 253).
// El catálogo real (FundamentalDto/AttitudeBehaviorDto) ya trae `icon`/`image` opcionales
// desde el backend; cuando el backend todavía no los puebla, se cae a los assets copiados
// en F0 (ver docs/paridad/INVENTARIO-ASSETS.md §2-3).
//
// H-90 — El backend NO devuelve rutas en `icon`: devuelve CLASES FontAwesome
// (`fa-brain`, `fa-bullseye`, `fa-comments`, `fa-users`; `image` viene null). Al meterlas
// tal cual en `<img src>` el dev-server las resolvía contra el fallback del SPA
// (`GET /app/fa-brain` → 200 con index.html), así que las 4 pastillas del rail y las del
// panel salían ROTAS mostrando el alt ("Apren…", "Dueñ…", "Comu…", "Const…") en vez del
// icono. Por eso ahora:
//   1. Solo se acepta como `src` lo que REALMENTE es una ruta/URL de imagen.
//   2. Si no lo es, se resuelve el SVG de marca por COLOR del Fundamental — que es la
//      invariante visual del demo: el disco del icono lleva el mismo color que la
//      pastilla (`mindsets & behaviors.svg` es #f28400 sobre pastilla #E08A3E, etc.,
//      ver `mockData.ts` : 25-28 del demo). Mapear por color (y no por índice) mantiene
//      icono y pastilla en el mismo tono sea cual sea el orden que mande el backend.
//   3. Último recurso: fallback estable por índice.
// ─────────────────────────────────────────────────────────────────────────────
import type { FundamentalDto, AttitudeBehaviorDto } from '@/api/recognition';
import { asset, assetPersistido } from '@/utils/basePath';

/** Los 4 SVG de marca de Fundamentales, cada uno con su disco de color + glifo blanco. */
const ICON_BEHAVIORS = asset('/icons/fundamentales/mindsets & behaviors.svg'); // #f28400 naranja
const ICON_ALIGNMENT = asset('/icons/fundamentales/essentialism & alignment.svg'); // #332f81 morado
const ICON_CONSUMER = asset('/icons/fundamentales/passion for consumerism.svg'); // #e11f26 rojo
const ICON_TALENT = asset('/icons/fundamentales/talent ownership.svg'); // #20b15a verde

/** Mismo orden que FUNDAMENTAL_PALETTE (naranja, morado, rojo, verde). */
const FUNDAMENTAL_ICON_FALLBACK = [
  ICON_BEHAVIORS,
  ICON_ALIGNMENT,
  ICON_CONSUMER,
  ICON_TALENT,
];

/**
 * SVG de marca por tono. Cada icono trae su propio disco de color + glifo blanco, así que
 * se elige el que coincide con el color del Fundamental para reproducir el degradado
 * icono/pastilla del demo. Claves = hex normalizado (minúsculas, 6 dígitos).
 */
const FUNDAMENTAL_ICON_BY_COLOR: Record<string, string> = {
  // Naranja — paleta app (#F08521), asset de marca (#F28400), demo f1 (#E08A3E).
  '#f08521': ICON_BEHAVIORS,
  '#f28400': ICON_BEHAVIORS,
  '#e08a3e': ICON_BEHAVIORS,
  // Morado — paleta app (#342F82), asset de marca (#332F81), demo f2 (#3D3D8E).
  '#342f82': ICON_ALIGNMENT,
  '#332f81': ICON_ALIGNMENT,
  '#3d3d8e': ICON_ALIGNMENT,
  // Rojo — paleta app (#E11F26) = asset de marca, demo f3 (#D32F2F).
  '#e11f26': ICON_CONSUMER,
  '#d32f2f': ICON_CONSUMER,
  // Verde — paleta app (#20B15A) = asset de marca, demo f4 (#4CAF50).
  '#20b15a': ICON_TALENT,
  '#4caf50': ICON_TALENT,
};

/**
 * ¿Es una ruta/URL de imagen usable como `src`? Descarta las clases FontAwesome
 * (`fa-brain`, `fas fa-users`) y cualquier token que no apunte a un archivo.
 */
function isImageSrc(value: string | null | undefined): value is string {
  const v = value?.trim();
  if (!v) return false;
  if (v.startsWith('data:image/')) return true;
  if (/^(?:https?:)?\/\//.test(v)) return true;
  // Ruta relativa o absoluta del sitio, pero solo si termina en extensión de imagen.
  return /^\.{0,2}\//.test(v) && /\.(?:svg|png|jpe?g|webp|gif|avif)(?:[?#]|$)/i.test(v);
}

/**
 * Normaliza un hex a `#rrggbb` en minúsculas; null si no es un hex reconocible. Exportada porque
 * `postitShared.tsx` la reutiliza para resolver el pastel del papel del post-it por el mismo
 * criterio (color real del Fundamental, no índice del catálogo) — ver `getPostItPaperColor`.
 */
export function normalizeHex(color: string | null | undefined): string | null {
  const v = color?.trim().toLowerCase();
  if (!v) return null;
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/.exec(v);
  if (!m) return null;
  const hex = m[1];
  return hex.length === 3
    ? `#${hex[0]}${hex[0]}${hex[1]}${hex[1]}${hex[2]}${hex[2]}`
    : `#${hex}`;
}

/**
 * Icono de un Fundamental: el del catálogo si es una imagen real; si no, por color; si no, por índice.
 *
 * 🔴 Los dos primeros vienen del CATÁLOGO (Mongo), no del código, así que pasan por
 * `assetPersistido`: medido el 2026-09-05, `pmp_survey_service.fundamentals_catalog` guarda tres
 * rutas del sitio (`/assets/pasionporconsumidor.svg`, `wsensialismo`, `talentownership`) que sin
 * prefijo se piden a la RAÍZ del dominio — o sea, a QA — cuando el portal vive bajo `/dev`.
 * `assetPersistido` y no `asset` porque aquí puede llegar cualquier cosa: `isImageSrc` acepta también
 * `https://…` y `data:image/…`, y prefijar ésas daría `/dev/https://…`.
 *
 * No se migra la base a propósito: el mismo documento sirve en QA, dev y PROD. En QA y PROD el
 * helper es la identidad (`VITE_BASE = '/'`), así que allí esto no cambia nada.
 */
export function getFundamentalIcon(f: FundamentalDto, index = 0): string {
  if (isImageSrc(f.image)) return assetPersistido(f.image);
  if (isImageSrc(f.icon)) return assetPersistido(f.icon);
  const byColor = FUNDAMENTAL_ICON_BY_COLOR[normalizeHex(f.color) ?? ''];
  if (byColor) return byColor;
  return FUNDAMENTAL_ICON_FALLBACK[index % FUNDAMENTAL_ICON_FALLBACK.length];
}

/** Los 7 comportamientos de "Mindsets & Behaviors" en el demo (ReconocerPanel.tsx : 9-17). */
const BEHAVIOR_ICON_FALLBACK = [
  asset('/icons/behaviors/learner.svg'),
  asset('/icons/behaviors/player owner.svg'),
  asset('/icons/behaviors/psychological safety.svg'),
  asset('/icons/behaviors/collaborate effectively.svg'),
  asset('/icons/behaviors/quality of work.svg'),
  asset('/icons/behaviors/make others successful.svg'),
  asset('/icons/behaviors/explore & experiment.svg'),
];

/** Icono de un Comportamiento: el del catálogo si es una imagen real; si no, fallback por índice. */
export function getBehaviorIcon(b: AttitudeBehaviorDto, index = 0): string {
  if (isImageSrc(b.image)) return b.image;
  return BEHAVIOR_ICON_FALLBACK[index % BEHAVIOR_ICON_FALLBACK.length];
}
